import { WebSocketController } from '@frontend/core'
import { formatDiagnosticsWebsocketAndUpdateStore } from '@frontend/core/lib/domains/hardware'
import { useGetActiveSessionQuery } from '@frontend/core/lib/domains/sessions'
import { toast } from '@frontend/ui'
import { useEffect, useRef } from 'react'
import { selectMetricsServerWebsocketUrl } from '../../modules/servers'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import {
  buildAssignmentMap,
  buildMessageParts,
  computeTransitions,
  type ArmedStateMap,
  type NotificationCondition
} from './batteryOfflineNotifications'
import { useFormattedHardware } from './hooks'
import { DeviceToastMessage } from './toastContent'

/**
 * Each condition maps to the sonner toast variant that colours it: a recovery
 * is a success, an offline is an error, a critical battery is a warning.
 */
const TOAST_FN: Record<NotificationCondition, typeof toast.success> = {
  online: toast.success,
  offline: toast.error,
  battery: toast.warning
}

/** Device toasts auto-dismiss after this long (ms) — longer than the default. */
const TOAST_DURATION_MS = 5000

export const SportableHardware = () => {
  const dispatch = useAppDispatch()
  const wsUrl = useAppSelector(selectMetricsServerWebsocketUrl)

  // Connect to hardware stream //
  useEffect(() => {
    if (!wsUrl) return
    WebSocketController.connectWebSocket(wsUrl, 'diagnostics', (data) => {
      dispatch(formatDiagnosticsWebsocketAndUpdateStore({ ...data }))
    })

    return () => {
      WebSocketController.disconnectWebSocket('diagnostics')
    }
  }, [wsUrl])

  return null
}

/**
 * Always-on, pitchside-only watcher. On each diagnostics snapshot it fires a
 * toast when any in-scope device on the network (anchor, ball, or player tag,
 * assigned or not) crosses into critical battery, goes offline, or comes
 * online. Edge-triggered: one toast per entry into a state, re-armed on
 * recovery. All decision logic lives in `./batteryOfflineNotifications`; this
 * component only wires selectors → `computeTransitions` → `toast`.
 */
export const SportableBatteryNotifications = () => {
  const formattedHardware = useFormattedHardware()
  const { data: activeSession } = useGetActiveSessionQuery()
  const armedRef = useRef<ArmedStateMap>({})

  // Frame-driven: the debounce/settling windows in `computeTransitions` are
  // counted in diagnostics snapshots, not wall-clock time, so they only advance
  // when a new snapshot arrives — there are no timers. The stream pushes a full
  // snapshot per frame, so a pending offline/come-online toast fires once it has
  // been seen in enough consecutive frames; a fully stalled stream simply stops
  // advancing the count (acceptable — a dead stream is itself a visible
  // failure). Counting received frames rather than elapsed time means a lagging
  // hardware/backend pipeline can't let a stale first reading trip a toast.
  useEffect(() => {
    const assignmentMap = buildAssignmentMap(activeSession?.data?.players)
    const { toFire, nextArmed } = computeTransitions(
      armedRef.current,
      formattedHardware.devices.list,
      assignmentMap
    )

    toFire.forEach(({ id, condition }) => {
      const device = formattedHardware.devices.map[id]
      if (!device) return

      const parts = buildMessageParts(device, assignmentMap)
      if (!parts) return

      TOAST_FN[condition](
        <DeviceToastMessage parts={parts} condition={condition} />,
        {
          id: `${id}-${condition}`,
          duration: TOAST_DURATION_MS,
          closeButton: true,
          // No icon: the typed toast (success/error/warning) would otherwise
          // render sonner's default icon; `null` suppresses it so the condition
          // reads purely from the coloured text.
          icon: null
        }
      )
    })

    armedRef.current = nextArmed
  }, [formattedHardware, activeSession])

  // Reset the armed map on unmount only (e.g. the pitchSide flag toggles, or
  // React StrictMode's dev remount) so a remount re-establishes the baseline
  // rather than inheriting stale armed state. This MUST be its own
  // mount-scoped effect — folding it into the snapshot effect's cleanup would
  // wipe the armed map on every frame and break the edge-trigger.
  useEffect(
    () => () => {
      armedRef.current = {}
    },
    []
  )

  return null
}

export const SportableLivePositionData = () => {
  const servers = useAppSelector((state) => state.servers)

  // Listen to raw websocket and update strack buffer once strack has been successfully initiated //
  // useEffect(() => {
  //   WebSocketController.connectWebSocket(
  //     servers.urls.metricsServer.websocketUrl,
  //     'raw',
  //     (data: RawPacket) => {
  //       // Push the new packet into the global buffer.
  //       // Maybe do some formatting here and put it on a webworker
  //       globalCircularBuffer.push(data)
  //     },
  //     'tracking'
  //   )
  //   return () => {
  //     WebSocketController.disconnectWebSocket('raw', 'tracking')
  //   }
  // }, [])

  return null
}
